import {
  Box,
  Card,
  CardContent,
  Typography
} from "@mui/material";
import { 
  TrendingUp, 
  TrendingDown, 
  TrendingFlat
} from "@mui/icons-material";
import { formatBRL, formatNumber } from "../metrics/utils/formatters";

export default function ProductMetrics({ productData }) {
  if (!productData) {
    return null;
  }

  const getTrendIcon = (percentDifference) => {
    const diff = percentDifference || 0;
    if (diff > 10) return <TrendingUp sx={{ color: '#f44336', fontSize: 20 }} />;
    if (diff < -10) return <TrendingDown sx={{ color: '#4caf50', fontSize: 20 }} />;
    return <TrendingFlat sx={{ color: '#2196f3', fontSize: 20 }} />;
  };

  const getStatusInfo = (status) => {
    if (!status) return { text: 'Sem Referência', color: '#64748b' };
    if (status.includes('high')) return { text: 'Preço Alto', color: '#dc2626' };
    if (status.includes('low')) return { text: 'Preço Baixo', color: '#059669' };
    if (status.includes('benchmark')) return { text: 'Competitivo', color: '#3b82f6' };
    return { text: 'Sem Referência', color: '#64748b' };
  };

  // Extrair valores com fallbacks seguros
  const currentPrice = productData.current_price?.amount || 0;
  const suggestedPrice = productData.suggested_price?.amount || 0;
  const lowestPrice = productData.lowest_price?.amount || 0;
  const percentDifference = productData.percent_difference || 0;
  const ratio = productData.ratio || 0;
  const competitorsCount = productData.competitors_count || 0;
  const status = productData.status || '';
  
  const statusInfo = getStatusInfo(status);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexWrap: 'wrap', 
      gap: 3, 
      mb: 4 
    }}>
      {/* Preço Atual */}
      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
        <Card sx={{ 
          background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(148, 163, 184, 0.1)',
          borderRadius: '16px',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
            borderColor: 'rgba(255, 112, 67, 0.15)'
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, #ff7043 0%, #ff5722 100%)',
          }
        }}>
          <CardContent sx={{ p: 3, pt: 3.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em'
                }}
              >
                Preço Atual
              </Typography>
              <Box sx={{ 
                p: 1,
                borderRadius: '8px',
                backgroundColor: percentDifference >= 0 ? 'rgba(239, 68, 68, 0.08)' : 'rgba(34, 197, 94, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {getTrendIcon(percentDifference)}
              </Box>
            </Box>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700, 
                color: '#0f172a',
                mb: 1.5,
                fontSize: '1.875rem',
                lineHeight: 1.2
              }}
            >
              {formatBRL(currentPrice)}
            </Typography>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              borderRadius: '8px',
              backgroundColor: percentDifference >= 0 ? 'rgba(239, 68, 68, 0.05)' : 'rgba(34, 197, 94, 0.05)',
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: percentDifference >= 0 ? '#dc2626' : '#059669',
                  fontWeight: 600,
                  fontSize: '0.8rem'
                }}
              >
                {percentDifference >= 0 ? '+' : ''}{formatNumber(percentDifference, 1)}%
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  fontSize: '0.8rem'
                }}
              >
                vs sugerido
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Preço Sugerido */}
      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
        <Card sx={{ 
          background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(148, 163, 184, 0.1)',
          borderRadius: '16px',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
            borderColor: 'rgba(34, 197, 94, 0.15)'
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
          }
        }}>
          <CardContent sx={{ p: 3, pt: 3.5 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#64748b',
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                mb: 2
              }}
            >
              Preço Sugerido
            </Typography>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700, 
                color: '#0f172a',
                mb: 1.5,
                fontSize: '1.875rem',
                lineHeight: 1.2
              }}
            >
              {formatBRL(suggestedPrice)}
            </Typography>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              borderRadius: '8px',
              backgroundColor: 'rgba(34, 197, 94, 0.05)',
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  fontSize: '0.8rem'
                }}
              >
                Baseado em {competitorsCount} competidores
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Menor Preço */}
      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
        <Card sx={{ 
          background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(148, 163, 184, 0.1)',
          borderRadius: '16px',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
            borderColor: 'rgba(139, 92, 246, 0.15)'
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)',
          }
        }}>
          <CardContent sx={{ p: 3, pt: 3.5 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#64748b',
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                mb: 2
              }}
            >
              Menor Preço
            </Typography>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700, 
                color: '#0f172a',
                mb: 1.5,
                fontSize: '1.875rem',
                lineHeight: 1.2
              }}
            >
              {formatBRL(lowestPrice)}
            </Typography>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              borderRadius: '8px',
              backgroundColor: 'rgba(139, 92, 246, 0.05)',
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  fontSize: '0.8rem'
                }}
              >
                Oportunidade: {formatBRL(currentPrice - lowestPrice)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Status e Ratio */}
      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
        <Card sx={{ 
          background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(148, 163, 184, 0.1)',
          borderRadius: '16px',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
            borderColor: 'rgba(59, 130, 246, 0.15)'
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
          }
        }}>
          <CardContent sx={{ p: 3, pt: 3.5 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#64748b',
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                mb: 2
              }}
            >
              Status do Preço
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600, 
                color: statusInfo.color,
                mb: 1.5,
                fontSize: '1.5rem',
                lineHeight: 1.2
              }}
            >
              {statusInfo.text}
            </Typography>
            <Box sx={{ 
              p: 1.5,
              borderRadius: '8px',
              backgroundColor: 'rgba(59, 130, 246, 0.05)',
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  fontSize: '0.8rem'
                }}
              >
                Ratio: {formatNumber(ratio, 2)}x
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}